/** 모바일 — 고객 조회·등록. 카드를 누르면 회원권과 시술 이력(누가/언제/무엇을/몇 회차)을 보여준다. */
import React, { useMemo, useState } from 'react';
import { showAlert } from 'utils/toastBridge';
import {
  loadTable, saveTable, createUid, nextCode, digitsOnly, formatPhone, today, customerSummaries,
  passRemain, num,
} from 'shop/shopStore';
import { Page, H, CardBox, Field, Btn, Chip } from './mobileUi';

const MobileCustomer = ({ tc }) => {
  const [kw, setKw] = useState('');
  const [open, setOpen] = useState('');
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ customerNm: '', phone: '', remark: '' });
  const [tick, setTick] = useState(0);

  const list = useMemo(() => {
    const summary = customerSummaries(tc);
    const q = kw.trim().toLowerCase();
    return loadTable(tc, 'customer')
      .filter((c) => c.useYn !== 'N')
      .map((c) => ({ ...c, ...summary(c.customerUid) }))
      .filter((c) => !q || `${c.customerNm} ${c.customerNo} ${c.phone} ${c.remark}`.toLowerCase().includes(q))
      .sort((a, b) => String(a.customerNm).localeCompare(String(b.customerNm)));
  }, [tc, kw, tick]); // eslint-disable-line react-hooks/exhaustive-deps

  const detail = useMemo(() => {
    if (!open) return null;
    const services = new Map(loadTable(tc, 'service').map((s) => [s.serviceUid, s]));
    return {
      passes: loadTable(tc, 'pass').filter((p) => p.customerUid === open).map((p) => ({ ...p, sn: services.get(p.serviceUid)?.serviceNm || '-' })),
      records: loadTable(tc, 'record')
        .filter((r) => r.customerUid === open)
        .sort((a, b) => String(b.visitDt).localeCompare(String(a.visitDt)))
        .slice(0, 15)
        .map((r) => ({ ...r, sn: services.get(r.serviceUid)?.serviceNm || '-' })),
    };
  }, [tc, open, tick]); // eslint-disable-line react-hooks/exhaustive-deps

  const add = () => {
    const nm = form.customerNm.trim();
    const d = digitsOnly(form.phone);
    if (!nm) return showAlert('이름을 입력하세요.', 'warning');
    if (d && (d.length < 9 || d.length > 11)) return showAlert('핸드폰 번호 자릿수를 확인하세요.', 'warning');
    const all = loadTable(tc, 'customer');
    const dup = d && all.find((c) => digitsOnly(c.phone) === d);
    if (dup && !window.confirm(`같은 번호의 고객이 있습니다: ${dup.customerNm}\n그래도 등록할까요?`)) return undefined;
    saveTable(tc, 'customer', [...all, {
      customerUid: createUid('CUS'),
      customerNo: nextCode(all, 'customerNo', 'C', 4),
      customerNm: nm,
      phone: d,
      firstVisitDt: today(),
      remark: form.remark,
      useYn: 'Y',
    }]);
    setForm({ customerNm: '', phone: '', remark: '' });
    setAdding(false);
    setTick((t) => t + 1);
    return showAlert('등록했습니다.', 'success');
  };

  return (
    <Page>
      <H>고객</H>
      <Field>검색
        <input value={kw} onChange={(e) => setKw(e.target.value)} placeholder="이름·번호·핸드폰" />
      </Field>
      <Btn onClick={() => setAdding(!adding)}>{adding ? '닫기' : '＋ 새 고객 등록'}</Btn>
      {adding && (
        <CardBox>
          <Field>이름<input value={form.customerNm} onChange={(e) => setForm({ ...form, customerNm: e.target.value })} /></Field>
          <Field>핸드폰<input inputMode="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Field>
          <Field>비고<input value={form.remark} onChange={(e) => setForm({ ...form, remark: e.target.value })} /></Field>
          <Btn $primary onClick={add}>등록</Btn>
        </CardBox>
      )}
      {list.length === 0 && <span className="s">고객이 없습니다.</span>}
      {list.map((c) => (
        <CardBox key={c.customerUid} $tap onClick={() => setOpen(open === c.customerUid ? '' : c.customerUid)}>
          <span className="t">{c.customerNm} <Chip>{c.customerNo}</Chip></span>
          <span className="s">{formatPhone(c.phone) || '번호 없음'}{c.lastVisit ? ` · 최근 ${c.lastVisit}` : ''}</span>
          {c.passSummary && <span className="s">{c.passSummary}</span>}
          {c.remark && <span className="s">{c.remark}</span>}
          {open === c.customerUid && detail && (
            <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span className="t">회원권</span>
              {detail.passes.length === 0 && <span className="s">없음</span>}
              {detail.passes.map((p) => (
                <span className="s" key={p.passUid}>
                  {p.sn} {passRemain(p)}/{num(p.totalCnt)}회 · {p.status}{p.recurringYn === 'Y' ? ' · 정기결제' : ''}
                </span>
              ))}
              <span className="t">시술 이력</span>
              {detail.records.length === 0 && <span className="s">없음</span>}
              {detail.records.map((r) => (
                <span className="s" key={r.recordUid}>{String(r.visitDt).slice(0, 10)} · {r.sn}{r.sessionNo ? ` ${r.sessionNo}회차` : ''}</span>
              ))}
            </div>
          )}
        </CardBox>
      ))}
    </Page>
  );
};

export default MobileCustomer;
